// File: app/src/components/workout/LoadAdjustDialog.js
//
// Mid-session load editor (issue #76). Opened from the load readout inside the
// Coach ring, so correcting the weight is a tap on the number that is wrong
// rather than a trip back to the plan editor.
//
// The control that matters most here is SCOPE. A load you change halfway
// through an exercise is almost never a correction to every set — you went up,
// or the last set was too heavy and you came down — so the default reaches
// forward from the set in progress and leaves completed sets recording what was
// actually lifted. "All sets" stays available for the case the plan simply had
// the wrong number from the start.

import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import SegmentedToggle from "../ui/segmented-toggle";
import DraggableStepper from "../ui/draggable-stepper";
import { useWeightUnit } from "../../contexts/WeightUnitContext";
import { fromDisplayWeight, toDisplayWeight } from "../../utils/units";

const FIELD_LABEL_CLASS =
  "text-[10px] text-muted-foreground uppercase tracking-widest font-semibold";

// lb is a way of reading kg (#191): the load is still saved in kg.
const LOAD_UNITS = [
  { value: "kg", label: "kg" },
  { value: "lb", label: "lb" },
  { value: "intensityPct", label: "% 1RM" },
];

// Bar-loading granularity for kg (the smallest pair of plates most people
// own); whole steps of 5 for a percentage, which is as fine as a 1RM estimate
// is honest to.
const STEPPER = {
  weight: { step: 2.5, max: 500, unit: "kg" },
  lb: { step: 5, max: 1100, unit: "lb" },
  intensityPct: { step: 5, max: 100, unit: "% 1RM" },
};

const LoadAdjustDialog = ({ open, onOpenChange, context, onSubmit }) => {
  const [dim, setDim] = useState("weight");
  const [value, setValue] = useState(0);
  const [scope, setScope] = useState("onward");
  const preferredUnit = useWeightUnit();
  const [weightUnit, setWeightUnit] = useState(preferredUnit);

  const setCount = context?.setCount ?? 1;
  const setIndex = context?.setIndex ?? 0;
  const isMultiSet = setCount > 1;

  // Re-seed each time the dialog opens: the phase in progress has moved on
  // since it was last closed, and a stale figure here would be written back as
  // though it were the current prescription.
  useEffect(() => {
    if (!open || !context) return;
    setDim(context.dim || "weight");
    setValue(Number.isFinite(context.value) ? context.value : 0);
    setScope(isMultiSet ? "onward" : "all");
    setWeightUnit(preferredUnit);
  }, [open, context, isMultiSet, preferredUnit]);

  if (!context) return null;

  const inLb = dim === "weight" && weightUnit === "lb";
  const config = inLb ? STEPPER.lb : STEPPER[dim] || STEPPER.weight;
  const shownUnit = dim === "intensityPct" ? "intensityPct" : weightUnit;
  const pickUnit = (u) => {
    if (u === "intensityPct") {
      setDim("intensityPct");
      return;
    }
    setDim("weight");
    setWeightUnit(u);
  };

  const scopeOptions = [
    { value: "set", label: `Set ${setIndex + 1}` },
    { value: "onward", label: `Set ${setIndex + 1}+` },
    { value: "all", label: "All sets" },
  ];

  const submit = (nextValue) => {
    onSubmit({
      dim,
      value: nextValue,
      scope: isMultiSet ? scope : "all",
      // The phase this editor was opened against. Handing it back is what stops
      // a write landing on the next exercise when a timed phase auto-advances
      // while the dialog is open.
      target: context,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xs">
        <DialogHeader>
          <DialogTitle className="text-base leading-snug">
            {context.label || "Load"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isMultiSet
              ? `Set ${setIndex + 1} of ${setCount}`
              : "Adjust the load recorded for this exercise"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <Label className={FIELD_LABEL_CLASS}>Measured in</Label>
            <SegmentedToggle
              value={shownUnit}
              onChange={pickUnit}
              options={LOAD_UNITS}
              className="w-full"
              itemClassName="flex-1"
            />
          </div>

          <DraggableStepper
            label="Load"
            value={inLb ? toDisplayWeight(value, "lb") : value}
            onChange={(v) => setValue(inLb ? fromDisplayWeight(v, "lb") : v)}
            min={0}
            max={config.max}
            step={config.step}
            unit={config.unit}
            offLabel="NONE"
            prominent
          />

          {isMultiSet && (
            <div className="space-y-1">
              <Label className={FIELD_LABEL_CLASS}>Apply to</Label>
              <SegmentedToggle
                value={scope}
                onChange={setScope}
                options={scopeOptions}
                className="w-full"
                itemClassName="flex-1"
              />
              <p className="text-[10px] text-muted-foreground leading-snug">
                {scope === "set"
                  ? "Only this set changes."
                  : scope === "onward"
                    ? "This set and the ones after it. Completed sets keep what you lifted."
                    : "Every set, including the ones already done."}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {context.value != null && (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={() => submit(null)}
            >
              Clear
            </Button>
          )}
          <Button size="sm" onClick={() => submit(value)} disabled={value <= 0}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LoadAdjustDialog;
